import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/MembersGroup';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const MEMBERS_GROUP_API_REDUCER_KEY = 'members-groups';
export const MEMBERS_GROUP_PATH = 'members-groups';

export const membersGroupApi = createApi({
    reducerPath: MEMBERS_GROUP_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['MembersGroup', 'MembersGroups'],
    endpoints: (builder) => ({
        getMembersGroupList: builder.query<PaginationResult<Entity.MembersGroup>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['MembersGroups'],
        }),
        getMembersGroup: builder.query<Entity.MembersGroup, Entity.PrimaryMembersGroup>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = membersGroupNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ MEMBERS_GROUP_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['MembersGroup'],
        }),
        getMembersGroupMultiple: builder.query<Entity.MembersGroup, Entity.PrimaryMembersGroup[]>({
            query: (ids) => {
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }/ids${Entity.PrimaryMembersGroup.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['MembersGroups'],
        }),
        getMembersGroupPresentation: builder.query<PaginationResult<Entity.MembersGroup>, Entity.PresentationMembersGroupRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ MEMBERS_GROUP_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['MembersGroups'],
        }),
        getMembersGroupAdvancedPresentation: builder.query<PaginationResult<Entity.MembersGroup>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['MembersGroups'],
        }),
        
        insertMembersGroup: builder.mutation<Entity.MembersGroup, Entity.CreateMembersGroup>({
            query: (toInsert) => {
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    membersGroupApi.util.upsertQueryData('getMembersGroup', Entity.PrimaryMembersGroup.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['MembersGroups'],
        }),
        insertMultipleMembersGroup: builder.mutation<Entity.MembersGroup[], Entity.CreateMembersGroup[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            membersGroupApi.util.upsertQueryData('getMembersGroup', Entity.PrimaryMembersGroup.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['MembersGroups'],
        }),
        updateMembersGroup: builder.mutation<Entity.MembersGroup, Entity.UpdateEntityMembersGroup>({
            query: (toUpdate) => {
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['MembersGroups'],
        }),
        updateMultipleMembersGroup: builder.mutation<Entity.MembersGroup[], Entity.UpdateEntityMembersGroup[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['MembersGroups'],
        }),
        deleteMembersGroup: builder.mutation<Entity.MembersGroup, Entity.MembersGroup>({
            query: (toDelete) => {
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }/${Entity.PrimaryMembersGroup.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['MembersGroups'],
        }),
        deleteMultipleMembersGroup: builder.mutation<Entity.MembersGroup[], Entity.PrimaryMembersGroup[]>({
            query: (ids) => {
                return ({
                    url: `/${ MEMBERS_GROUP_PATH }/batch${Entity.PrimaryMembersGroup.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['MembersGroups'],
        }),
    }),
})

export const [membersGroupNormalizer, membersGroupNormalizerMiddleware] = createNormalizationMiddleware(MEMBERS_GROUP_API_REDUCER_KEY, membersGroupApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetMembersGroupListQuery,
    useGetMembersGroupMultipleQuery,
    useGetMembersGroupPresentationQuery,
    useGetMembersGroupQuery,
    useInsertMembersGroupMutation,
    useInsertMultipleMembersGroupMutation,
    useUpdateMembersGroupMutation,
    useUpdateMultipleMembersGroupMutation,
    useDeleteMembersGroupMutation,
    useDeleteMultipleMembersGroupMutation,
    useGetMembersGroupAdvancedPresentationQuery,
    
} = membersGroupApi
